import { ActivationStatus } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { useAdminAccountSnapshotByMonthQuery } from '../../../../queries/Admin/AccountSnapshot/useAdminAccountSnapshotByMonthQuery';
import {
	invalidateAdminAccountSnapshotByMonthRenewalSummaryQuery,
	useAdminAccountSnapshotByMonthRenewalSummaryQuery,
} from '../../../../queries/Admin/AccountSnapshot/useAdminAccountSnapshotByMonthRenewalSummaryQuery';
import { useAdminAccountSnapshotDeleteMuataion } from '../../../../queries/Admin/AccountSnapshot/useAdminAccountSnapshotDeleteMutation';
import { AccountSnapshotViewModel, IAccountFilterCriteria } from '../../../../viewmodels/AdminViewModels';
import {
	ConfirmationDialog,
	DefaultDeleteConfirmationOptions,
	IConfirmationDialogOption,
} from '../../../../web/components/ConfirmationDialog';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { ComputerCoffeeGraphic } from '../../../../web/components/svgs/graphics/ComputerCoffeeGraphic';
import { WarningIcon } from '../../../../web/components/svgs/icons/WarningIcon';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { RenewalSummaryHeader } from '../../../components/reporting/RenewalSummaryHeader';
import { ReportRowAccount } from '../../../components/reporting/ReportRowAccount';
import { BillingStatusOptions } from '../ReportsMonthlyRenewal/filterOptions';
import { styleSheet } from './styles';

interface IProps {
	filter?: IAccountFilterCriteria;
	unassignedCsmFilter?: boolean;
	billingStatusFilter?: BillingStatusOptions;
}

export const MonthlyRenewalSnapshots: React.FC<IProps> = observer(
	({ filter, unassignedCsmFilter, billingStatusFilter }) => {
		const errorMessages = useErrorMessages();
		const userSession = useUserSession();
		const logger = useEventLogging('MonthlyRenewalSnapshots');
		const [snapshotToDelete, setSnapshotToDelete] = React.useState<AccountSnapshotViewModel | null>(null);
		const adminAccountSnapshotDeleteMutation = useAdminAccountSnapshotDeleteMuataion({
			onError: error => {
				errorMessages.pushApiError(error);
				logger.logApiError('DeleteAccountSnapshotError', error);
			},
			onSuccess: () => {
				toast.success('Snapshot successfully deleted');
				invalidateAdminAccountSnapshotByMonthRenewalSummaryQuery();
			},
		});
		const adminAccountSnapshotByMonthQuery = useAdminAccountSnapshotByMonthQuery({ filter });
		const renewalSummaryQuery = useAdminAccountSnapshotByMonthRenewalSummaryQuery({ filter });
		const snapshotVms = React.useMemo(
			() =>
				(adminAccountSnapshotByMonthQuery.data ?? []).map(
					snapshot => new AccountSnapshotViewModel(userSession, snapshot)
				),
			[adminAccountSnapshotByMonthQuery?.data, userSession]
		);

		// Function to check if a snapshot has an unassigned CSM
		const hasUnassignedCsm = (snapshot: AccountSnapshotViewModel) => {
			const accountModel = snapshot.accountModel;
			const customerSuccessName = accountModel?.additionalInfo?.customerSuccessName;
			return !customerSuccessName || customerSuccessName === '';
		};

		// Apply filters
		const filteredSnapshotVms = React.useMemo(() => {
			let filtered = snapshotVms;

			if (unassignedCsmFilter) {
				filtered = filtered.filter(hasUnassignedCsm);
			}

			// Apply billing status filter
			if (billingStatusFilter && billingStatusFilter !== BillingStatusOptions.All) {
				filtered = filtered.filter(snapshot => {
					const currentBillingStatus = snapshot?.toJs()?.currentBillingStatus?.toLowerCase();
					const isPastDue = currentBillingStatus === 'past_due';
					const isCancelled =
						snapshot?.accountCancelled || snapshot?.accountModel?.activationStatus === ActivationStatus.CANCELED;
					const isActive = !isPastDue && !isCancelled;

					switch (billingStatusFilter) {
						case BillingStatusOptions.PastDue:
							return isPastDue;
						case BillingStatusOptions.Cancelled:
							return isCancelled;
						case BillingStatusOptions.Active:
							return isActive;
						default:
							return true;
					}
				});
			}

			return filtered;
		}, [snapshotVms, unassignedCsmFilter, billingStatusFilter]);

		const onRequestRefreshRenewalData = () => {
			invalidateAdminAccountSnapshotByMonthRenewalSummaryQuery();
		};

		const onDeleteClicked = (snapshot: AccountSnapshotViewModel) => {
			setSnapshotToDelete(snapshot);
		};

		const onDeleteConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
			if (!canceled && !!result?.representedObject && !!snapshotToDelete) {
				adminAccountSnapshotDeleteMutation.mutate({ id: snapshotToDelete?.id });
			}
			setSnapshotToDelete(null);
		};

		const isLoading = adminAccountSnapshotByMonthQuery.isPending || renewalSummaryQuery.isPending;

		return (
			<div
				className={`${css(styleSheet.container)} monthly-renewal-snapshots`}
				data-scroll-restoration-id='admin-monthly-renewal-report-snapshot-table'
			>
				<RenewalSummaryHeader />
				{isLoading ? (
					<div className={css(baseStyleSheet.flexCenter)}>
						<LoadingSpinner type='large' />
					</div>
				) : (
					filteredSnapshotVms?.length > 0 &&
					filteredSnapshotVms.map(snapshot => {
						return (
							<ReportRowAccount
								snapshot={snapshot}
								key={snapshot?.id}
								onRequestRefreshRenewData={onRequestRefreshRenewalData}
								onDeleteClicked={() => onDeleteClicked(snapshot)}
							/>
						);
					})
				)}{' '}
				{adminAccountSnapshotByMonthQuery.isSuccess && filteredSnapshotVms.length === 0 ? (
					<>
						<div className={css(baseStyleSheet.flexCenter)}>
							<ComputerCoffeeGraphic />
						</div>
						<div className={css(styleSheet.noneFound)}>No available snapshots for renewals this month.</div>
					</>
				) : null}
				<ConfirmationDialog
					icon={<WarningIcon />}
					modalProps={{
						isOpen: !!snapshotToDelete,
						onRequestClose: onDeleteConfirmationRequestClose,
					}}
					options={DefaultDeleteConfirmationOptions}
					title={`Are you sure you want to delete the "${
						snapshotToDelete?.accountModel?.companyName || ''
					}" snapshot for this month?`}
				/>
			</div>
		);
	}
);
